import { FC, useEffect, useState, KeyboardEvent } from 'react';
import { useStyles } from './styles';
import { useStore } from 'hooks/useRootStore';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { useMutation, useQuery } from 'react-query';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import {
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton,
  Paper,
  OutlinedInput,
} from '@material-ui/core';
import { Link } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { useHistory, useParams } from 'react-router';
import EndUserHeader from 'components/Headers/EndUserHeader';
import { handleCompanyUrl, handleOrganizationUrl } from 'utils/organizationUtils';
import { OrganizationParamUrl } from './types';
import EndUserFooter from 'components/Headers/EndUserFooter/EndUserFooter';
import Easy from '../../assets/easyidentity.jpg';

const SignInEndUser: FC = observer(() => {
  const classes = useStyles();
  const history = useHistory();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [loginUrl, setLoginUrl] = useState<string | null>(null);
  const [registerCase, setRegisterCase] = useState<string>('');
  const [forgetCase, setForgetCase] = useState<string>('');
  const {
    endUserSettingsFormStore: { getPortalSettings, endUserConfigSettings, getSetting, settingsByTypeView },
    userStore: { login, googlelogin },
    notificationStore: { openNotification },
  } = useStore();

  useEffect(() => {
    document.title = 'EasyIdentity: User Login';
  }, []);

  const { domain } = useParams<OrganizationParamUrl>();

  const portalSettingsgetReq = useQuery('portalSettingsgetReq', () => getPortalSettings(), {
    enabled: !!domain && domain !== 'undefined' && domain !== 'null',
    onError: () => openNotification('error', `Can't get data`),
  });

  const getSettingsReq = useQuery('getSettingReq', () => getSetting(), {
    enabled: !!domain && domain !== 'undefined' && domain !== 'null',
    onError: () => openNotification('error', `Can't get policy`),
  });

  const googleLoginReq = useQuery('getUsersReq', () => googlelogin(), {
    enabled: !!domain && domain !== 'undefined' && domain !== 'null',
    onError: () => openNotification('error', `Can't login users`),
  });

  useEffect(() => {
    document.title = 'EasyIdentity: User Login';
  }, []);

  const handleEnterPressed = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.code === 'Enter') {
      loginRequest.mutate();
    }
  };

  useEffect(() => {
    if (googleLoginReq.data) {
      setLoginUrl(googleLoginReq.data);
    }
  }, [googleLoginReq]);

  useEffect(() => {
    settingsByTypeView('general').forEach((group: any, i: any) =>
      group.settings.forEach((setting: any, j: any) => {
        if (setting.description === 'Enable Self-Service Account Registration') {
          return setRegisterCase(setting.value);
        } else if (setting.description === `Enable Self-Service UserID/Password Recovery`) {
          return setForgetCase(setting.value);
        } else {
          return 0;
        }
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getSettingsReq.isFetching]);

  const [loader, setLoader] = useState<boolean>(false);
  const loginRequest = useMutation(() => login({ email, password, type: 'user' }), {
    mutationKey: 'loginRequest',
    onSuccess: async (response) => {
      if (endUserConfigSettings?.googleAuthentication === 'false') {
        response?.data?.data?.user?.permission !== 'EndUser'
          ? history.push(handleCompanyUrl('/dashboard'))
          : history.push(handleCompanyUrl('/admin/profile'));
      } else if (response?.data.data?.user?.isTwoFactorEnable === false) {
        history.push(handleCompanyUrl('/admin/profile'));
      } else {
        const fqdn = response?.data?.data?.organizationName;
        const token = response?.data?.data?.token;
        if (!fqdn || !token) {
          throw new Error('Missing organization name or token.');
        }
        setLoader(true);
        const fetchData = async () => {
          const apiUrl = `${process.env.REACT_APP_API_BASE_URL}authenticator/enable`;
          const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
              Accept: 'application/json',
              fqdn: fqdn,
            },
          });
          if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
          }
          const responseData = await response.json();
          const qrCodeUrl = responseData.qrCodeUrl;
          const secretKey = responseData.secretKey;
          if (qrCodeUrl) {
            localStorage.setItem('QRCode', qrCodeUrl);
            localStorage.setItem('secretKey', secretKey);
            setLoader(false);
            history.push(handleCompanyUrl('/2-fa'));
          } else {
            setLoader(false);
            history.push(handleCompanyUrl('/authenticate'));
          }
        };
        fetchData();
      }
    },
    onError: ({ message }: Error) => {
      openNotification('error', message || 'Something went wrong');
      setLoader(false);
    },
  });

  useAppPreloader(
    loginRequest.isLoading ||
      googleLoginReq.isFetching ||
      loader ||
      getSettingsReq.isFetching ||
      portalSettingsgetReq.isFetching
  );

  return (
    <>
      {!domain ? (
        <div className={classes.containers}>
          <div className={classes.alertContainer}>
            <div className={classes.alertboxes}>
              <Grid className={classes.alertmessage}>
                <div className={classes.alertbox}>
                  <div>
                    <ErrorOutlineIcon className={classes.alertIcon} />
                  </div>
                  <div>
                    <div className={classes.alertTitle}>Please contact your Administrator for the correct URL.</div>
                  </div>
                </div>
              </Grid>
            </div>
          </div>
        </div>
      ) : (
        <Grid
          item
          className={classes.container}
          onKeyDownCapture={handleEnterPressed}
          style={{ backgroundImage: `url(${endUserConfigSettings?.bannerImage || Easy})`, backgroundSize: 'cover' }}
        >
          <EndUserHeader />
          {endUserConfigSettings?.title && (
            <Grid className={classes.alertmessage}>
              <div className={classes.alertbox}>
                <div>
                  <ErrorOutlineIcon className={classes.alertIcon} />
                </div>
                <div>
                  <div className={classes.alertTitle}>{endUserConfigSettings.title}</div>
                  <div className={classes.alertContent}>{endUserConfigSettings.description}</div>
                </div>
              </div>
            </Grid>
          )}
          (
          <>
            <Grid container className={classes.main} justify='center' spacing={2}>
              <Grid item>
                <Paper
                  className={endUserConfigSettings?.title ? `${classes.paper}` : `${classes.paperres}`}
                  elevation={3}
                >
                  <h1 className={classes.title}>Please Sign-In</h1>
                  <Grid container justify='center'>
                    <TextField
                      className={classes.formControl}
                      color='primary'
                      id='email'
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      required
                      fullWidth
                      variant='outlined'
                      label='Email address'
                      autoFocus
                    />
                    <FormControl className={classes.formControl} required>
                      <InputLabel variant='outlined' htmlFor='password'>
                        Password
                      </InputLabel>
                      <OutlinedInput
                        id='password'
                        label='password'
                        fullWidth
                        required
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(event) => setPassword(event.target.value)}
                        endAdornment={
                          <InputAdornment position='end'>
                            <IconButton
                              aria-label='toggle password visibility'
                              onClick={() => setShowPassword(!showPassword)}
                            >
                              {showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        }
                      />
                    </FormControl>
                  </Grid>
                  <Grid className={classes.formControlLinks} container justify='space-between'>
                    {forgetCase === '1' && (
                      <Grid className={classes.forgotPassword}>
                        <Link className={classes.link} to={handleOrganizationUrl(domain, '/user/forgot-password')}>
                          Forgot Password?
                        </Link>
                      </Grid>
                    )}
                    {registerCase === '1' && (
                      <Grid className={classes.forgotPassword}>
                        New User?
                        <Link
                          className={classes.link}
                          to={handleOrganizationUrl(domain, '/register')}
                          style={{ marginLeft: '10px' }}
                        >
                          Register
                        </Link>
                      </Grid>
                    )}
                  </Grid>

                  <Grid className={classes.formControl} container justify='space-between'>
                    <Button
                      className={classes.button}
                      variant='contained'
                      color='primary'
                      onClick={() => {
                        loginRequest.mutate();
                      }}
                    >
                      Sign-In
                    </Button>
                    {endUserConfigSettings?.googleLogin === 'true' && (
                      <>
                        <span className={classes.titles}>OR</span>
                        <Button className={classes.loginWithGoogleBtn}>
                          {loginUrl != null && <a href={loginUrl}>Sign in with Google</a>}
                        </Button>
                      </>
                    )}
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
            <EndUserFooter />
          </>
          )
        </Grid>
      )}
    </>
  );
});

export default SignInEndUser;
